import { useEffect, createContext, type PropsWithChildren, useCallback, useState } from 'react';

import axios from 'axios';
import { useContainer } from 'unstated-next';

import AdminAuthContainer from './AdminAuthContainer';
import { GET_CLASS_DASHBOARD_DATA } from '../../config/apiPaths';
import { getUserUuidHeaders } from '../../utils/httpUtils';
import { getUrls, processApiPath } from '../../utils/url';

export interface ClassProviderProps {
    classUuid: string;
    onBack?: () => void;
    narrowStyle?: boolean;
    fullScreen?: boolean;
}

interface ClassContextInterface {
    classUuid: string | null;
    loading: boolean;
    isNarrowStyle: boolean;
    isFullScreen: boolean;
    errorMessage: string;
    errorModalMessage: string;
    setErrorModalMessage: (message: string) => void;
    openErrorModal: boolean;
    setOpenErrorModal: (openEditModal: boolean) => void;
    classData: any;
    setClassData: (classData: any) => void;
    onBack?: () => void;
    me: any;
    meFetched: boolean;
}

const ClassContext = createContext(undefined as unknown as ClassContextInterface);

const { Provider, Consumer } = ClassContext;

const ClassProvider = (props: PropsWithChildren<ClassProviderProps>) => {
    const search = new URLSearchParams(window.location.search);
    const userUuid = search.get('user_uuid') || window.localStorage.getItem('user_uuid');

    const { authMode, showDemoContent } = useContainer(AdminAuthContainer);
    const { fullScreen, narrowStyle, classUuid, children } = props;
    const [isLoading, setIsLoading] = useState(true);
    const [classData, setClassData] = useState<any>({ loading: true, class_sessions: [] });
    const [openErrorModal, setOpenErrorModal] = useState<boolean>(false);
    const [errorModalMessage, setErrorModalMessage] = useState<string>('');
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [me, setMe] = useState<any>({});
    const [meFetched, setMeFetched] = useState<boolean>(false);
    const { api } = getUrls();

    console.log('classProvider classUuid', classUuid);

    const fetchClassData = useCallback(async () => {
        try {
            // should get extra data not base data
            const { data } = await axios.get(
                `${api}${GET_CLASS_DASHBOARD_DATA}?user_uuid=${userUuid}&class_uuid=${classUuid}&auth_mode=${authMode}&demo_content=${showDemoContent}`
            );
            setClassData(data);
            setIsLoading(false);
        } catch (err) {
            setErrorMessage(err!.toString());
            setClassData({ loading: false, class_sessions: [] });
            setIsLoading(false);
        }
    }, [api, authMode, classUuid, userUuid, showDemoContent]);

    useEffect(() => {
        if (!classUuid) {
            return;
        }
        if (meFetched) {
            return;
        }
        const fetchMe = async () => {
            const getMeResponse = await axios.get(
                processApiPath(GET_CLASS_DASHBOARD_DATA, undefined, {
                    class_uuid: classUuid,
                    auth_mode: authMode,
                    demo_content: String(showDemoContent)
                }),
                { headers: getUserUuidHeaders(userUuid as string) }
            );
            setMe(getMeResponse);
            setMeFetched(true);
            fetchClassData();
        };

        fetchMe();
    }, [api, userUuid, fetchClassData, authMode, classUuid, meFetched, showDemoContent]);

    return (
        <Provider
            value={{
                classUuid,
                loading: classData.loading || isLoading,
                isFullScreen: !!fullScreen,
                isNarrowStyle: !!narrowStyle,
                openErrorModal,
                setOpenErrorModal,
                errorModalMessage,
                setErrorModalMessage,
                classData,
                setClassData,
                me,
                meFetched,
                errorMessage
            }}
        >
            {children}
        </Provider>
    );
};

export { ClassContext, ClassProvider, Consumer as ClassConsumer };
